import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  resolveDonationDisplayImageUrl,
  resolveLocalizedName,
} from 'pi-kiosk-shared';
import { CatalogImagePlaceholder } from 'pi-kiosk-shared/ui';
import { toCustomerFacingErrorMessage } from '../../../../shared/errors/toCustomerFacingErrorMessage.js';
import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import { DonationProjectCard } from '../../../donation-projects/components/DonationProjectCard.js';
import { formatMinorAmount } from '../../hooks/usePhoneFirstDonationJourney.js';
import type { UsePhoneFirstDonationJourneyResult } from '../../hooks/usePhoneFirstDonationJourney.js';

export type PhoneFirstDonationProjectStepViewProps = Pick<
  UsePhoneFirstDonationJourneyResult,
  | 't'
  | 'tenantCode'
  | 'defaultWhicheverProject'
  | 'whicheverError'
  | 'submitWhicheverProject'
  | 'projects'
  | 'submitProject'
  | 'amountMinor'
  | 'numberLocale'
>;

export function PhoneFirstDonationProjectStepView(
  props: PhoneFirstDonationProjectStepViewProps,
): JSX.Element {
  const {
    t,
    tenantCode,
    defaultWhicheverProject,
    whicheverError,
    submitWhicheverProject,
    projects,
    submitProject,
    numberLocale,
  } = props;
  const { i18n } = useTranslation('customer');
  const whicheverDisplayName =
    defaultWhicheverProject !== null
      ? resolveLocalizedName(
          defaultWhicheverProject.name,
          defaultWhicheverProject.nameLocales,
          i18n.language,
        )
      : null;

  const defaultWhicheverImageUrl = useMemo(() => {
    if (defaultWhicheverProject === null) {
      return null;
    }
    return resolveDonationDisplayImageUrl(
      defaultWhicheverProject.thumbnailUrl ?? null,
      defaultWhicheverProject.imageUrl ?? null,
      { tenantCode },
    );
  }, [defaultWhicheverProject, tenantCode]);

  const safeWhicheverError =
    whicheverError != null
      ? toCustomerFacingErrorMessage(whicheverError, {
          fallback: t('errors.donationWhicheverNotConfigured'),
        })
      : null;

  return (
    <>
      <Card padded>
        <div className="flex flex-col gap-3">
          <div className="relative h-32 w-full overflow-hidden rounded-md bg-[var(--color-surface-muted)]">
            {defaultWhicheverImageUrl !== null && defaultWhicheverImageUrl.length > 0 ? (
              <img
                src={defaultWhicheverImageUrl}
                alt={whicheverDisplayName ?? t('donation.anyProject')}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <CatalogImagePlaceholder
                label={t('donation.anyProject')}
                className="h-full w-full"
              />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-[var(--color-on-surface)]">
              {t('donation.anyProject')}
            </h3>
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {t('donation.anyProjectHint')}
            </p>
          </div>
          {safeWhicheverError !== null ? (
            <p className="text-sm text-[var(--color-danger)]" role="alert">
              {safeWhicheverError}
            </p>
          ) : null}
          <Button intent="primary" size="lg" block onClick={submitWhicheverProject}>
            {t('donation.anyProject')}
          </Button>
        </div>
      </Card>

      <h3 className="px-1 text-sm font-medium text-[var(--color-on-surface-muted)]">
        {t('donation.orChooseSpecific')}
      </h3>

      {(() => {
        if (projects.isLoading) {
          return (
            <Card padded>
              <p className="text-sm text-[var(--color-on-surface-muted)]">
                {t('donation.loadingProjects')}
              </p>
            </Card>
          );
        }
        if (projects.isError || projects.data === undefined) {
          return (
            <Card padded>
              <p className="text-sm text-[var(--color-danger)]" role="alert">
                {t('donation.projectsLoadError')}
              </p>
              <Button
                intent="secondary"
                size="md"
                block
                className="mt-3"
                onClick={() => {
                  void projects.refetch();
                }}
              >
                {t('shared.retry')}
              </Button>
            </Card>
          );
        }
        if (!Array.isArray(projects.data)) {
          return (
            <Card padded>
              <p className="text-sm text-[var(--color-danger)]" role="alert">
                {t('donation.projectsLoadError')}
              </p>
            </Card>
          );
        }
        return projects.data.map((p) => (
          <DonationProjectCard
            key={p.id}
            project={p}
            numberLocale={numberLocale}
            onSelect={() => submitProject(p.code)}
          />
        ));
      })()}
    </>
  );
}

export function donationProjectSubtitle(amountMinor: number, numberLocale: string): string {
  return formatMinorAmount(amountMinor, 'CZK', numberLocale);
}
